import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon } from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

// Interface matches the transformed data from GET /clients
interface Client {
  id: number;
  companyName: string; // Updated from 'name'
  director: string;   
  email?: string;
  status: string;
  // Add potentially missing fields from API response
  currentDiscount?: number;
  totalOrders?: number;
  totalSpent?: number;
  // ... other potential fields
}

// New interface for the API response structure
interface PaginatedClientsResponse {
  clients: Client[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

// Define available statuses for filtering
const CLIENT_STATUSES = {
  all: 'Все статусы',
  active: 'Активный',
  inactive: 'Неактивный',
  potential: 'Потенциальный'
};

const ClientList: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- State for Pagination, Sorting, Filtering ---
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(15);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'companyName'); // Default sort
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'ASC'); // Default order

  // const canCreateClients = can('clients.create'); // Will use can('clients.create') directly

  // --- Fetch Clients Function with Params ---
  const fetchClients = useCallback(async (page = currentPage, search = searchTerm, status = statusFilter, sort = sortBy, order = sortOrder) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    params.append('sortBy', sort);
    params.append('sortOrder', order);
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);

    setSearchParams(params, { replace: true }); // Update URL

    try {
      const response = await axios.get<PaginatedClientsResponse>('/clients', { params });
      setClients(response.data.clients);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Ошибка при загрузке данных о клиентах');
      setClients([]); // Clear clients on error
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, statusFilter, sortBy, sortOrder]); // Add dependencies

  // Trigger fetch when parameters change
  useEffect(() => {
    fetchClients(currentPage, searchTerm, statusFilter, sortBy, sortOrder);
  }, [fetchClients, currentPage, searchTerm, statusFilter, sortBy, sortOrder]);

  // --- Handlers ---
  const handleDelete = async (id: number) => {
    if (!can('clients.delete')) return; // Changed from canManageClients
    if (window.confirm('Вы уверены, что хотите удалить этого клиента?')) {
      try {
        await axios.delete(`/clients/${id}`);
        // Refetch current page data after deletion
        fetchClients(currentPage, searchTerm, statusFilter, sortBy, sortOrder);
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Ошибка при удалении клиента');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleSort = (newSortBy: string) => {
    const newSortOrder = sortBy === newSortBy && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page on sort change
  };

  // --- Helper Functions ---
  const formatDiscount = (discount: number | undefined | null) => {
    if (discount === undefined || discount === null || discount <= 0) return 'Нет';
    return `${discount}%`;
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '0 ₸';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderSortIcon = (columnName: string) => {
    if (sortBy !== columnName) {
      return null; // Or a default subtle icon
    }
    return sortOrder === 'ASC' ? (
      <ArrowUpIcon className="h-4 w-4 ml-1 inline-block text-gray-600" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 ml-1 inline-block text-gray-600" />
    );
  };

  // --- Render Logic ---
  if (loading && clients.length === 0) { // Show spinner only on initial load
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Клиенты"
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          can('clients.create') ? ( // Changed from canManageClients
            <Link
              to="/clients/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить клиента
            </Link>
          ) : null
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      {/* Filters Row */}
      <div className="mb-6 flex flex-wrap gap-4 items-end">
        {/* Search Box */}
        <div className="flex-grow sm:flex-grow-0 sm:w-64">
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                    id="search"
                    type="text"
                    placeholder="Имя, контакт, телефон..."
                    className="form-input pl-10"
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
            </div>
        </div>
        {/* Status Filter */}
        <div className="flex-grow sm:flex-grow-0 sm:w-48">
          <label htmlFor="statusFilter" className="form-label">Статус</label>
          <select 
              id="statusFilter"
              className="form-input"
              value={statusFilter}
              onChange={handleStatusChange}
          >
             {Object.entries(CLIENT_STATUSES).map(([value, label]) => (
                 <option key={value} value={value}>{label}</option>
             ))}
          </select>
        </div>
      </div>

      {/* Clients table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Sortable Headers */}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('companyName')}>
                  Компания {renderSortIcon('companyName')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('director')}>
                  Директор {renderSortIcon('director')} {/* Changed header and sort key */}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Контакты
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('currentDiscount')}>
                  Скидка {renderSortIcon('currentDiscount')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('totalOrders')}>
                  Заказов {renderSortIcon('totalOrders')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('totalSpent')}>
                  Всего потрачено {renderSortIcon('totalSpent')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && clients.length > 0 && ( // Show subtle loading row
                <tr><td colSpan={7} className="p-4 text-center text-gray-500"><LoadingSpinner /> Загрузка...</td></tr>
              )}
              {!loading && clients.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-500">Клиенты не найдены</td></tr>
              )}
              {!loading && clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words"> 
                    <Link to={`/clients/${client.id}`} className="text-primary hover:underline">
                      {client.companyName}
                    </Link>
                  </td>
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 text-sm text-gray-900 break-words"> 
                    {client.director} {/* Changed data field */}
                  </td>
                  {/* Removed whitespace-nowrap and text-ellipsis from div, added break-words to td */}
                  <td className="px-6 py-4 text-sm text-gray-500 break-words"> 
                    {/* Removed phone display */}
                    <div>{client.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {client.currentDiscount && client.currentDiscount > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {formatDiscount(client.currentDiscount)}
                      </span>
                    ) : (
                      <span className="text-gray-500">Нет</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-500">
                    {client.totalOrders || 0}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                    {formatCurrency(client.totalSpent)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <Link
                        to={`/clients/${client.id}`}
                        className="text-primary hover:text-primary-dark p-1"
                        title="Детали"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      {can('clients.edit') && ( // Changed from canManageClients
                        <Link
                          to={`/clients/edit/${client.id}`}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="Редактировать"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                      )}
                      {can('clients.delete') && ( // Changed from canManageClients
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Удалить"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls - Moved here */}
        {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
              />
            </div>
        )}
      </div>
    </div>
  );
};

export default ClientList;
